import * as React from 'react'
import {
  Table,
  TableOptions,
  RowSelectionState,
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  Row,
  RowData,
} from '@tanstack/react-table'
import { VirtualItem, useVirtualizer } from '@tanstack/react-virtual'
import { useId, useControlled, useEventCallback } from '../../hooks'
import { setRef } from '../../utils'
import { ApiRef } from './useSelectAdvanceApiRef'

export type SelectAdvanceInputChangeReason = 'input' | 'reset' | 'clear'

export type SelectAdvanceHighlightChangeReason =
  | 'keyboard'
  | 'mouse'
  | 'auto'
  | 'touch'

export type SelectAdvanceCloseReason =
  | 'toggleInput'
  | 'escape'
  | 'selectOption'
  | 'removeOption'
  | 'blur'

export interface OptionsProps {
  key: string
  label: any
  tabIndex: number
  role: string
  id: string
  onMouseMove: (event: React.SyntheticEvent) => void
  onClick: (event: React.KeyboardEvent<HTMLElement>) => void
  onTouchStart: (event: React.SyntheticEvent) => void
  isDisabled: boolean
  'data-option-index': number
  'aria-disabled': boolean
  'aria-selected': boolean
}

export interface UseSelectAdvanceProps<Value>
  extends Pick<
    TableOptions<Value>,
    | 'data'
    | 'getRowId'
    | 'getSubRows'
    | 'getRowCanExpand'
    | 'enableSubRowSelection'
    | 'getIsRowExpanded'
  > {
  /**
   * If `true`, the input's text is cleared on blur if no value is selected.
   *
   * Set to `true` if you want to help the user enter a new value.
   * Set to `false` if you want to help the user resume their search.
   * @default true
   */
  clearOnBlur?: boolean
  /**
   * @internal The prefix of the state class name
   * @default 'factor'
   */
  classNamePrefix?: string
  /**
   * If `true`, the first option is automatically highlighted.
   * @default false
   */
  autoHighlight?: boolean
  /**
   * The component name that is using this hook. Used for warnings.
   */
  componentName?: string
  /**
   * If `true`, the popup won't close when a value is selected.
   * @default false
   */
  disableCloseOnSelect?: boolean
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, will allow focus on disabled items.
   * @default false
   */
  disabledItemsFocusable?: boolean
  /**
   * If `true`, the list box in the popup will not wrap focus.
   * @default true
   */
  disableListWrap?: boolean
  /**
   * Used to determine the disabled state for a given option.
   *
   * @param {Value} option The option to test.
   * @returns {boolean}
   */
  getOptionDisabled?: (option: Value) => boolean
  /**
   * Render the selected value.
   * You can only use it when the `native` prop is `false` (default).
   *
   * @param {any} value The `value` provided to the component.
   * @returns {ReactNode}
   */
  renderValue?: (value: Value | Value[]) => React.ReactNode
  /**
   * Used to determine the string value for a given option.
   * It's used to fill the input (and the list box options if `renderOption` is not provided).
   *
   * If used in free solo mode, it must accept both the type of the options and a string.
   *
   * @param {Value} option
   * @returns {string}
   * @default (option) => option.label ?? option
   */
  getOptionLabel?: (option: Value) => string
  /**
   * If `true`, the component handles the "Home" and "End" keys when the popup is open.
   * It should move focus to the first option and last option, respectively.
   */
  handleHomeEndKeys?: boolean
  /**
   * This prop is used to help implement the accessibility logic.
   * If you don't provide an id it will fall back to a randomly generated one.
   */
  id?: string
  /**
   * The input value.
   */
  inputValue?: string
  /**
   * If `true`, `value` must be an array and the menu will support multiple selections.
   * @default false
   */
  multiple?: boolean
  /**
   * Callback fired when the popup requests to be closed.
   * Use in controlled mode (see open).
   *
   * @param {React.SyntheticEvent} event The event source of the callback.
   * @param {string} reason Can be: `"toggleInput"`, `"escape"`, `"selectOption"`, `"removeOption"`, `"blur"`.
   */
  onClose?: (
    event: React.SyntheticEvent,
    reason: SelectAdvanceCloseReason
  ) => void
  /**
   * Callback fired when the highlight option changes.
   *
   * @param {React.SyntheticEvent} event The event source of the callback.
   * @param {Value} option The highlighted option.
   * @param {string} reason Can be: `"keyboard"`, `"auto"`, `"mouse"`, `"touch"`.
   */
  onHighlightChange?: (
    event: React.SyntheticEvent,
    option: Value | null,
    reason: SelectAdvanceHighlightChangeReason
  ) => void
  /**
   * Callback fired when the input value changes.
   *
   * @param {React.SyntheticEvent} event The event source of the callback.
   * @param {string} value The new value of the text input.
   * @param {string} reason Can be: `"input"` (user input), `"reset"` (programmatic change), `"clear"`.
   */
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: SelectAdvanceInputChangeReason
  ) => void
  /**
   * Callback fired when the popup requests to be opened.
   * Use in controlled mode (see open).
   *
   * @param {React.SyntheticEvent} event The event source of the callback.
   */
  onOpen?: (event: React.SyntheticEvent) => void
  /**
   * If `true`, the component is shown.
   */
  open?: boolean
  /**
   * If `true`, the component becomes readonly. It is also supported for multiple tags where the tag cannot be deleted.
   * @default false
   */
  readOnly?: boolean
  /**
   * Selected Rows State
   */
  value: RowSelectionState
  /**
   * default Select Rows
   */
  defaultValue?: RowSelectionState
  /**
   * Set the height in pixel of a row in the grid.
   * @default 52
   */
  rowHeight?: number
  /**
   * overscan rows for virtualization
   * @default 3
   */
  overscan?: number
  /**
   * Sets the page size
   * @default 20
   */
  pageSize?: number
  /**
   * Set the total number of rows required for lazy-loading if not passed onFetchRows callback will not be called
   */
  rowCount: number
  /**
   * If `true`, a  loading overlay is displayed.
   */
  loading?: boolean
  /**
   * Callback fired when rowCount is set and the next batch of virtualized rows is rendered.
   */
  onFetchRows?: (args: {
    startIndex: number
    endIndex: number
    pageSize: number
    search: string
  }) => unknown
  /**
   * Callback fired when a row has subRows available
   */
  onFetchSubRows?: (args: {
    row: Row<Value>
    insertNestedData: <T extends Record<string, any>>({
      existingRows,
      subRowsToInsert,
      path,
      getRowId,
      subRowKey,
    }: {
      existingRows: T[]
      subRowsToInsert: T[]
      path: string[]
      getRowId: (row: T) => string
      subRowKey: string
    }) => T[]
  }) => unknown
  /**
   * onChange handler
   */
  onChange: TableOptions<Value>['onRowSelectionChange']
  /**
   * Define the shape of data by default it will be label, value
   */
  columns?: TableOptions<Value>['columns']
  /**
   * defaultExpanded will expand all the expandable rows by default
   * @default: false
   */
  defaultExpanded?: boolean
  /**
   * apiRef will provide internal tableInstance and virtualizerInstance
   */
  apiRef?: React.MutableRefObject<ApiRef<Value>>
  /**
   * rowLoading contains the rowIds that are loading children data
   */
  isRowLoading?: RowSelectionState
  /**
   * allows clearing selection for a single select
   * @default false
   */
  allowClearingSingleSelect?: boolean
}

export function useSelectAdvance<Value>(props: UseSelectAdvanceProps<Value>) {
  const {
    apiRef,
    clearOnBlur = true,
    classNamePrefix = 'factor',
    autoHighlight = false,
    componentName = 'useAutocomplete',
    disableCloseOnSelect = false,
    disabled: disabledProp,
    disabledItemsFocusable = false,
    disableListWrap = true,
    getOptionDisabled,
    getOptionLabel: getOptionLabelProp = (option: Value) =>
      // @ts-ignore
      option?.label ?? option,
    handleHomeEndKeys = true,
    id: idProp,
    inputValue: inputValueProp,
    multiple = false,
    onClose,
    onHighlightChange,
    onInputChange,
    onOpen,
    open: openProp,
    readOnly,
    data = [],
    value,
    defaultValue,
    onChange,
    overscan = 3,
    pageSize = 20,
    rowCount: totalRows,
    loading,
    onFetchRows,
    // @ts-ignore
    getRowId = data => data?.value,
    getSubRows,
    getRowCanExpand,
    enableSubRowSelection,
    getIsRowExpanded,
    rowHeight = 52,
    isRowLoading,
    defaultExpanded,
    allowClearingSingleSelect = false,
    columns = [
      {
        accessorKey: 'value',
      },
      {
        accessorKey: 'label',
      },
    ],
    renderValue = value => {
      value = Array.isArray(value) ? value : [value]
      const result = value.map(one => {
        return getOptionLabelProp(one)
      })
      return result.join(', ')
    },
    // @ts-ignore internal prop
    hasOriginalOnChange,
  } = props

  const isActiveElementInListbox = (listboxRef: React.RefObject<HTMLElement>) =>
    listboxRef.current !== null &&
    listboxRef.current.parentElement?.contains(document.activeElement)

  const id = useId(idProp)

  let getOptionLabel = getOptionLabelProp

  getOptionLabel = option => {
    const optionLabel = getOptionLabelProp(option)
    if (typeof optionLabel !== 'string') {
      if (process.env.NODE_ENV !== 'production') {
        const erroneousReturn =
          optionLabel === undefined
            ? 'undefined'
            : `${typeof optionLabel} (${optionLabel})`
        console.error(
          `The \`getOptionLabel\` method of ${componentName} returned ${erroneousReturn} instead of a string for ${JSON.stringify(
            option
          )}.`
        )
      }
      return String(optionLabel)
    }
    return optionLabel
  }

  const render = React.useState(0)[1]
  const inputRef = React.useRef<HTMLElement>(null)
  const listboxRef = React.useRef<HTMLElement>(null)
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const defaultHighlighted = autoHighlight ? 0 : -1
  const highlightedIndexRef = React.useRef(defaultHighlighted)
  const tableInstance = useReactTable({
    data: data,
    columns,
    getRowId: getRowId,
    getSubRows: getSubRows,
    getIsRowExpanded: getIsRowExpanded,
    getRowCanExpand: getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableMultiRowSelection: multiple,
    enableSubRowSelection: enableSubRowSelection,
    initialState: {
      rowSelection: defaultValue ?? {},
      // @ts-ignore
      expanded: defaultExpanded,
    },
    ...(value ? { state: { rowSelection: value } } : {}),
    ...(hasOriginalOnChange && typeof onChange === 'function'
      ? { onRowSelectionChange: onChange }
      : {}),
  })
  const rows = tableInstance.getRowModel().rows
  const skeletonSize = React.useMemo(
    () =>
      totalRows && data.length < totalRows
        ? totalRows - data.length < pageSize
          ? totalRows - data.length
          : pageSize
        : loading
          ? pageSize
          : 0,
    [totalRows, pageSize, loading, data.length]
  )

  const virtualRow = useVirtualizer({
    count: rows.length + skeletonSize,
    overscan: overscan,
    getScrollElement: () => listboxRef.current,
    estimateSize: () => rowHeight,
  })

  React.useImperativeHandle(
    apiRef,
    () => ({
      tableInstance,
      virtualizer: virtualRow,
    }),
    [tableInstance, virtualRow]
  )

  const skeletonVisible = React.useMemo(
    () =>
      (virtualRow.range?.endIndex ?? 0) >=
      (rows.length > 0 ? rows.length - 1 : rows.length),
    [virtualRow.range?.endIndex, rows.length]
  )

  const [inputValue, setInputValueState] = useControlled({
    controlled: inputValueProp,
    default: '',
    name: componentName,
    state: 'inputValue',
  })

  React.useEffect(() => {
    if (
      skeletonVisible &&
      skeletonSize &&
      loading === false &&
      typeof onFetchRows === 'function'
    ) {
      onFetchRows({
        startIndex: (virtualRow.range?.startIndex ?? 0) + 1,
        endIndex: (virtualRow.range?.endIndex ?? 0) + skeletonSize,
        pageSize,
        search: inputValue,
      })
    }
  }, [
    onFetchRows,
    virtualRow,
    skeletonVisible,
    skeletonSize,
    pageSize,
    loading,
    tableInstance,
    inputValue,
  ])

  const getDisplayValue = useEventCallback(() => {
    const selectedIds = Object.keys(tableInstance.getState().rowSelection ?? {})
    if (selectedIds.length) {
      const rowByIds = tableInstance.getCoreRowModel().rowsById
      const selectedRows = selectedIds.map(
        one => rowByIds[one]?.original ?? ({} as Value)
      )
      return renderValue(selectedRows)
    }
  })

  const getSelectAll = useEventCallback(() => {
    const selectedIds = tableInstance
      .getCoreRowModel()
      .flatRows.reduce((prev, current) => {
        if (!current.getCanExpand()) {
          prev[current.id] = true
        }
        return prev
      }, {} as RowSelectionState)
    tableInstance.setRowSelection(() => selectedIds)
  })

  const getClearAll = useEventCallback(() => {
    tableInstance.setRowSelection(() => ({}) as RowSelectionState)
  })

  const getIsAllSelected = useEventCallback(() => {
    const allRows = tableInstance.getCoreRowModel().flatRows
    for (let i = 0; i < allRows.length; i++) {
      const currentRow = allRows[i]
      if (!currentRow?.getCanExpand() && !currentRow?.getIsSelected()) {
        return false
      }
    }
    return true
  })

  const [open, setOpenState] = useControlled({
    controlled: openProp,
    default: false,
    name: componentName,
    state: 'open',
  })

  const listboxAvailable = open && rows.length > 0 && !readOnly
  const popupOpen = open && !readOnly

  const handleListboxRef = useEventCallback(node => {
    if (!node) {
      return
    }
    setRef(listboxRef, node)
    render(prev => prev + 1)
  })

  // Focus the input when interacting with the combobox
  const handleClick = (event: React.ChangeEvent<HTMLElement>) => {
    // Prevent focusing the input if click is anywhere outside the Autocomplete
    if (!event.currentTarget.contains(event.target)) {
      return
    }
    inputRef.current?.focus()
  }

  const handleClose = (
    event: React.SyntheticEvent<Element, Event>,
    reason: SelectAdvanceCloseReason
  ) => {
    if (!open) {
      return
    }
    setOpenState(false)
    if (onClose) {
      onClose(event, reason)
    }
  }

  const handleOpen = (event: React.SyntheticEvent<Element, Event>) => {
    if (open) {
      return
    }
    setOpenState(true)
    if (onOpen) {
      onOpen(event)
    }
    inputRef.current?.focus()
  }

  const resetInputValue = React.useCallback(
    (event: React.SyntheticEvent<HTMLElement, Event>) => {
      if (!clearOnBlur) {
        return
      }
      setInputValueState('')

      if (onInputChange) {
        onInputChange(event, '', 'reset')
      }
    },
    [onInputChange, setInputValueState, clearOnBlur]
  )

  const clearInputValue = React.useCallback(
    (event: React.SyntheticEvent<HTMLElement, Event>) => {
      setInputValueState('')
      if (onInputChange) {
        onInputChange(event, '', 'clear')
      }
    },
    [onInputChange, setInputValueState]
  )

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value

    if (inputValue !== newValue) {
      setInputValueState(newValue)

      if (onInputChange) {
        onInputChange(event, newValue, 'input')
      }
    }
  }

  const selectNewValue = (
    event: React.KeyboardEvent<HTMLElement>,
    option: Row<Value>,
    reasonProp: SelectAdvanceCloseReason = 'selectOption'
  ) => {
    if (!option.getCanSelect()) {
      return
    }
    toggleSelected(
      tableInstance,
      option,
      undefined,
      undefined,
      allowClearingSingleSelect
    )
    resetInputValue(event)
    if (
      !multiple &&
      !disableCloseOnSelect &&
      (!event || (!event.ctrlKey && !event.metaKey))
    ) {
      handleClose(event, reasonProp)
    }
  }

  function validOptionIndex(index: number, direction: 'next' | 'previous') {
    if (!listboxRef.current || index === -1) {
      return -1
    }
    let nextFocus = index
    const rows = tableInstance.getRowModel().rows
    // eslint-disable-next-line
    while (true) {
      // Out of range
      if (
        (direction === 'next' && nextFocus === rows.length) ||
        (direction === 'previous' && nextFocus === -1)
      ) {
        return -1
      }
      const row = rows[index]
      let isDisabled = false
      if (row) {
        isDisabled = getOptionDisabled?.(row!.original) ?? false
      }
      // Same logic as MenuList.js
      const nextFocusDisabled = disabledItemsFocusable ? false : isDisabled

      if (nextFocusDisabled) {
        // Move to the next element.
        nextFocus += direction === 'next' ? 1 : -1
      } else {
        return nextFocus
      }
    }
  }

  const setHighlightedIndex = useEventCallback(
    ({
      event,
      index,
      reason = 'auto',
    }: {
      event: React.SyntheticEvent<Element, Event>
      index: number
      reason: SelectAdvanceHighlightChangeReason
    }) => {
      highlightedIndexRef.current = index
      if (index === -1) {
        inputRef.current?.removeAttribute('aria-activedescendant')
      } else {
        inputRef.current?.setAttribute(
          'aria-activedescendant',
          `${id}-option-${index}`
        )
      }
      const rows = tableInstance.getRowModel().rows
      if (onHighlightChange) {
        onHighlightChange(
          event,
          index === -1 ? null : rows[index]!.original,
          reason
        )
      }
      if (!listboxRef.current) {
        return
      }

      const prev = listboxRef.current?.querySelector(
        `[role="option"].${classNamePrefix}-focused`
      )
      if (prev) {
        prev.classList.remove(`${classNamePrefix}-focused`)
        prev.classList.remove(`${classNamePrefix}-focusVisible`)
      }
      let listboxNode: HTMLElement | null | undefined = listboxRef.current
      if (listboxRef.current.getAttribute('role') !== 'listbox') {
        listboxNode =
          listboxRef.current.parentElement?.querySelector('[role="listbox]')
      }

      // "No result"
      if (!listboxNode) {
        return
      }

      const option = listboxRef.current.querySelector(
        `[data-option-index="${index}"]`
      )

      if (!option) {
        if (index !== 0) {
          virtualRow.scrollToIndex(index, { align: 'start' })
        } else {
          virtualRow.scrollToIndex(index, { align: 'end' })
        }
        setTimeout(() => {
          if (listboxRef.current) {
            const option = listboxRef.current.querySelector(
              `[data-option-index="${index}"]`
            )
            if (option) {
              option.classList.add(`${classNamePrefix}-focused`)
              if (reason === 'keyboard') {
                option.classList.add(`${classNamePrefix}-focusVisible`)
              }
            }
          }
        })
        return
      }

      option.classList.add(`${classNamePrefix}-focused`)
      if (reason === 'keyboard') {
        option.classList.add(`${classNamePrefix}-focusVisible`)
      }
      if (
        listboxNode.scrollHeight > listboxNode.clientHeight &&
        reason !== 'mouse' &&
        reason !== 'touch'
      ) {
        const scrollBottom = listboxNode.clientHeight + listboxNode.scrollTop
        const virtualElement = virtualRow
          .getVirtualItems()
          .filter(one => one.index === index)[0]
        const elementBottom = virtualElement?.end ?? 0
        const elementStart = virtualElement?.start ?? 0
        if (elementBottom > scrollBottom) {
          virtualRow.scrollToIndex(index, { align: 'end' })
        } else if (elementStart < listboxNode.scrollTop) {
          virtualRow.scrollToIndex(index, { align: 'start' })
        }
      }
    }
  )

  const changeHighlightedIndex = useEventCallback(
    ({
      event,
      diff,
      direction = 'next',
      reason = 'auto',
    }: {
      event: any
      diff: number | 'reset' | 'start' | 'end'
      direction: 'next' | 'previous'
      reason: SelectAdvanceHighlightChangeReason
    }) => {
      if (!popupOpen) {
        return
      }

      const getNextIndex = () => {
        const maxIndex = tableInstance.getRowModel().rows.length - 1

        if (diff === 'reset') {
          return defaultHighlighted
        }

        if (diff === 'start') {
          return 0
        }

        if (diff === 'end') {
          return maxIndex
        }

        const newIndex = highlightedIndexRef.current + diff

        if (newIndex < 0) {
          if (
            (disableListWrap && highlightedIndexRef.current !== -1) ||
            Math.abs(diff) > 1
          ) {
            return 0
          }
          return maxIndex
        }

        if (newIndex > maxIndex) {
          if (disableListWrap || Math.abs(diff) > 1) {
            return maxIndex
          }
          return 0
        }

        return newIndex
      }

      const nextIndex = validOptionIndex(getNextIndex(), direction)
      setHighlightedIndex({ index: nextIndex, reason, event })
    }
  )

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    // @ts-ignore
    if (event.defaultFactorPrevented) {
      return
    }

    // Wait until IME is settled.
    if (event.which !== 229) {
      switch (event.key) {
        case 'Home':
          if (popupOpen && handleHomeEndKeys) {
            // Prevent scroll of the page
            event.preventDefault()
            changeHighlightedIndex({
              diff: 'start',
              direction: 'next',
              reason: 'keyboard',
              event,
            })
          }
          break
        case 'End':
          if (popupOpen && handleHomeEndKeys) {
            // Prevent scroll of the page
            event.preventDefault()
            changeHighlightedIndex({
              diff: 'end',
              direction: 'previous',
              reason: 'keyboard',
              event,
            })
          }
          break
        case 'PageUp':
          // Prevent scroll of the page
          event.preventDefault()
          changeHighlightedIndex({
            diff: -pageSize,
            direction: 'previous',
            reason: 'keyboard',
            event,
          })
          handleOpen(event)
          break
        case 'PageDown':
          // Prevent scroll of the page
          event.preventDefault()
          changeHighlightedIndex({
            diff: pageSize,
            direction: 'next',
            reason: 'keyboard',
            event,
          })
          handleOpen(event)
          break
        case 'ArrowDown':
          // Prevent cursor move
          event.preventDefault()
          changeHighlightedIndex({
            diff: 1,
            direction: 'next',
            reason: 'keyboard',
            event,
          })
          handleOpen(event)
          break
        case 'ArrowUp':
          // Prevent cursor move
          event.preventDefault()
          changeHighlightedIndex({
            diff: -1,
            direction: 'previous',
            reason: 'keyboard',
            event,
          })
          handleOpen(event)
          break
        case 'Enter':
          if (event.shiftKey) {
            const option = rows[highlightedIndexRef.current]
            if (option && option.getCanExpand()) {
              option.getToggleExpandedHandler()()
              event.preventDefault()
            }
          } else if (highlightedIndexRef.current !== -1 && popupOpen) {
            const option = rows[highlightedIndexRef.current]
            const disabled =
              option && getOptionDisabled
                ? getOptionDisabled(option.original)
                : false
            const canSelect = option?.getCanSelect() ?? false
            // Avoid early form validation, let the end-users continue filling the form.
            event.preventDefault()
            if (disabled || !canSelect) {
              return
            }
            selectNewValue(event, option!, 'selectOption')
          }
          break
        case 'Escape':
          if (popupOpen) {
            // Avoid Opera to exit fullscreen mode.
            event.preventDefault()
            // Avoid the Modal to handle the event.
            event.stopPropagation()
            handleClose(event, 'escape')
            anchorEl?.focus()
          }
          break
        default:
      }
    }
  }

  const handleOptionMouseMove =
    (option: Row<Value>, virtualItem: VirtualItem) =>
    (event: React.SyntheticEvent) => {
      const index = virtualItem.index
      if (highlightedIndexRef.current !== index) {
        setHighlightedIndex({
          event,
          index,
          reason: 'mouse',
        })
      }
    }

  const handleOptionTouchStart =
    (option: Row<Value>, virtualItem: VirtualItem) =>
    (event: React.SyntheticEvent) => {
      const index = virtualItem.index
      setHighlightedIndex({
        event,
        index,
        reason: 'touch',
      })
    }

  const handleOptionClick =
    (option: Row<Value>) => (event: React.KeyboardEvent<HTMLElement>) => {
      selectNewValue(event, option, 'selectOption')
    }

  const handlePopupIndicator = (event: React.SyntheticEvent<Element>) => {
    if (open) {
      handleClose(event, 'toggleInput')
    } else {
      handleOpen(event)
    }
  }

  const handleBlur = (event: React.SyntheticEvent<HTMLElement>) => {
    // Ignore the event when using the scrollbar with IE11
    if (isActiveElementInListbox(listboxRef)) {
      inputRef.current?.focus()
      return
    }

    if (clearOnBlur) {
      resetInputValue(event)
    }

    handleClose(event, 'blur')
  }

  // Prevent input blur when interacting with the combobox
  const handleMouseDown = (event: React.ChangeEvent) => {
    // Prevent focusing the input if click is anywhere outside the Autocomplete
    if (!event.currentTarget.contains(event.target)) {
      return
    }
    if (event.target.getAttribute('id') !== id) {
      event.preventDefault()
    }
  }

  const handleKeyDownRoot = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!readOnly) {
      const validKeys = [
        ' ',
        'ArrowUp',
        'ArrowDown',
        // The native select doesn't respond to enter on macOS, but it's recommended by
        // https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
        'Enter',
      ]

      if (validKeys.indexOf(event.key) !== -1) {
        event.preventDefault()
        update(true, event)
      }
    }
  }

  const handleMouseDownRoot = (event: React.MouseEvent<HTMLDivElement>) => {
    // Ignore everything but left-click
    if (event.button !== 0) {
      return
    }
    // Hijack the default focus behavior.
    event.preventDefault()
    update(!open, event)
    anchorEl?.focus()
  }

  const update = (open: boolean, event: React.SyntheticEvent<HTMLElement>) => {
    if (open) {
      handleOpen(event)
      setTimeout(() => inputRef.current?.focus())
    } else {
      handleClose(event, 'toggleInput')
    }
  }

  return {
    getRootProps: (other: any = {}) => ({
      'aria-owns': listboxAvailable ? `${id}-listbox` : null,
      ...other,
      role: 'button',
      onKeyDown: handleKeyDownRoot,
      onMouseDown: disabledProp || readOnly ? undefined : handleMouseDownRoot,
      disabled: disabledProp,
      tabIndex: 0,
    }),
    getPaperProps: (other: any = {}) => ({
      ...other,
      onKeyDown: handleKeyDown,
      onClick: handleClick,
      onMouseDown: handleMouseDown,
    }),
    getInputProps: () => ({
      id,
      value: inputValue,
      onChange: handleInputChange,
      onBlur: handleBlur,
      // if open then this is handled imperatively so don't let react override
      // only have an opinion about this when closed
      'aria-activedescendant': popupOpen ? '' : undefined,
      'aria-controls': listboxAvailable ? `${id}-listbox` : undefined,
      'aria-expanded': listboxAvailable,
      // Disable browser's suggestion that might overlap with the popup.
      // Handle autocomplete but not autofill.
      autoComplete: 'off',
      inputRef: inputRef,
      autoCapitalize: 'none',
      spellCheck: false,
      role: 'combobox',
      disabled: disabledProp,
    }),
    getPopupIndicatorProps: () => ({
      tabIndex: -1,
      onClick: handlePopupIndicator,
    }),
    getListboxProps: () => ({
      role: 'listbox',
      id: `${id}-listbox`,
      'aria-labelledby': `${id}-label`,
      ref: handleListboxRef,
      onMouseDown: (event: React.SyntheticEvent) => {
        // Prevent blur
        event.preventDefault()
      },
    }),
    getOptionProps: (row: Row<Value>, virtualItem: VirtualItem) => {
      const disabled = getOptionDisabled?.(row.original) ?? false
      return {
        key: row.id,
        label: getOptionLabel(row.original),
        tabIndex: -1,
        role: 'option',
        id: `${row.id}-option-${row.index}`,
        onMouseMove: handleOptionMouseMove(row, virtualItem),
        onClick: handleOptionClick(row),
        onTouchStart: handleOptionTouchStart(row, virtualItem),
        isDisabled: disabled,
        'data-option-index': virtualItem.index,
        'aria-disabled': disabled,
        'aria-selected': row.getIsSelected(),
      }
    },
    rows,
    virtualRow,
    tableInstance,
    id,
    inputValue,
    value,
    expanded: popupOpen && anchorEl,
    popupOpen,
    anchorEl,
    setAnchorEl,
    getDisplayValue,
    isRowLoading,
    getSelectAll,
    getClearAll,
    getIsAllSelected,
    clearInputValue,
    handleClose,
    isEmpty:
      Object.keys(tableInstance.getState().rowSelection ?? {}).length > 0
        ? false
        : true,
  }
}

const toggleSelected = <T,>(
  table: Table<T>,
  row: Row<T>,
  value?: boolean,
  opts?: { selectChildren?: boolean },
  allowClearingSingleSelect?: boolean
) => {
  const isSelected = row.getIsSelected()

  table.setRowSelection(old => {
    value = typeof value !== 'undefined' ? value : !isSelected

    let skipCheck = true

    if (row.getCanExpand()) {
      const { rowSelection } = table.getState()
      const areChildrenSelected = isSubRowSelected(row, rowSelection, table)
      if (areChildrenSelected) {
        value = false
        skipCheck = true
      }
    }

    if (!skipCheck && row.getCanSelect() && isSelected === value) {
      return old
    }

    const selectedRowIds = { ...old }
    const copySelectedRowIds = { ...old }

    mutateRowIsSelected(
      selectedRowIds,
      row.id,
      value,
      opts?.selectChildren ?? true,
      table
    )

    if (
      !row.getCanMultiSelect() &&
      allowClearingSingleSelect === false &&
      Object.keys(selectedRowIds).length <= 0
    ) {
      return copySelectedRowIds
    }
    return selectedRowIds
  })
}

const mutateRowIsSelected = <TData extends RowData>(
  selectedRowIds: Record<string, boolean>,
  id: string,
  value: boolean,
  includeChildren: boolean,
  table: Table<TData>
) => {
  const row = table.getRow(id)

  if (value) {
    if (!row.getCanMultiSelect()) {
      Object.keys(selectedRowIds).forEach(key => delete selectedRowIds[key])
    }
    // We skip rows that have children from the selection
    if (row.getCanSelect() && !row.getCanExpand()) {
      selectedRowIds[id] = true
    }
  } else {
    delete selectedRowIds[id]
  }

  if (includeChildren && row.subRows?.length && row.getCanSelectSubRows()) {
    row.subRows.forEach(row =>
      mutateRowIsSelected(selectedRowIds, row.id, value, includeChildren, table)
    )
  }
}

export function isRowSelected<TData extends RowData>(
  row: Row<TData>,
  selection: Record<string, boolean>
): boolean {
  return selection[row.id] ?? false
}

export function isSubRowSelected<TData extends RowData>(
  row: Row<TData>,
  selection: Record<string, boolean>,
  table: Table<TData>
): boolean | 'some' | 'all' {
  if (!row.subRows?.length) return false

  let allChildrenSelected = true
  let someSelected = false

  row.subRows.forEach(subRow => {
    // Bail out early if we know both of these
    if (someSelected && !allChildrenSelected) {
      return
    }

    if (subRow.getCanSelect() && !subRow.getCanExpand()) {
      if (isRowSelected(subRow, selection)) {
        someSelected = true
      } else {
        allChildrenSelected = false
      }
    }

    // Check row selection of nested subrows
    if (subRow.subRows && subRow.subRows.length) {
      const subRowChildrenSelected = isSubRowSelected(subRow, selection, table)
      if (subRowChildrenSelected === 'all') {
        someSelected = true
      } else if (subRowChildrenSelected === 'some') {
        someSelected = true
        allChildrenSelected = false
      } else {
        allChildrenSelected = false
      }
    } else if (subRow.getCanExpand()) {
      allChildrenSelected = false
    }
  })

  return allChildrenSelected ? 'all' : someSelected ? 'some' : false
}
